import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "@/data/content";
import usePageContent from "@/hooks/usePageContent";
import useSettings from "@/hooks/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const PROJECT_TYPES = [
    "Mobilier sur mesure",
    "Cuisine personnalisée",
    "Dressing & rangements",
    "Rénovation intérieure",
];

export default function ContactCTA() {
    const t = usePageContent("home");
    const settings = useSettings();
    const contact = settings.contact || {};
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        project_type: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!form.name || !form.email || !form.message) {
            toast.error("Merci de remplir les champs requis.");
            return;
        }
        try {
            setSubmitting(true);
            await axios.post(`${API}/quote-requests`, form);
            setSubmitted(true);
            toast.success("Demande envoyée — nous vous recontactons sous 48h.");
            setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Une erreur est survenue. Veuillez réessayer.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 md:py-32 overflow-hidden bg-[#0d1b2e]"
        >
            <img
                src={IMAGES.cta}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d1b2e]/85 via-[#0d1b2e]/90 to-[#0d1b2e]/95" />

            <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-20">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1, ease }}
                        className="lg:col-span-5 text-white"
                    >
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#DC2626]" />
                            <span className="overline text-white/70">{t("contact.overline", "06 · Contact")}</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            {t("contact.title", "Parlons de")}<br />
                            <span className="italic">{t("contact.title_italic", "votre projet.")}</span>
                        </h2>
                        <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-md">
                            {t(
                                "contact.subtitle",
                                "Transformez votre intérieur avec du mobilier sur mesure pensé pour durer. Devis gratuit, sans engagement.",
                            )}
                        </p>

                        <div className="mt-12 space-y-5 text-white/85">
                            <div>
                                <div className="overline text-white/50 mb-1">Adresse</div>
                                <div className="font-serif text-xl">{t("contact.address", contact.address)}</div>
                            </div>
                            <div>
                                <div className="overline text-white/50 mb-1">Téléphone</div>
                                <a href={`tel:${(t("contact.phone", contact.phone) || "").replace(/\s/g, "")}`} className="font-serif text-xl link-underline" data-testid="contact-phone">
                                    {t("contact.phone", contact.phone)}
                                </a>
                            </div>
                            <div>
                                <div className="overline text-white/50 mb-1">Email</div>
                                <a
                                    href={`mailto:${t("contact.email", contact.email)}`}
                                    className="font-serif text-xl link-underline"
                                    data-testid="contact-email"
                                >
                                    {t("contact.email", contact.email)}
                                </a>
                            </div>
                        </div>
                    </motion.div>

                    <motion.form
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1, delay: 0.1, ease }}
                        onSubmit={onSubmit}
                        data-testid="quote-form"
                        className="lg:col-span-7 lg:col-start-6"
                    >
                        {submitted ? (
                            <div
                                data-testid="quote-success"
                                className="border border-white/15 p-10 md:p-14 text-white"
                            >
                                <div className="overline text-white/60 mb-4">Demande reçue</div>
                                <h3 className="font-serif text-3xl md:text-4xl mb-6">
                                    Merci pour votre confiance.
                                </h3>
                                <p className="text-white/70 leading-relaxed max-w-lg">
                                    Notre studio prendra contact avec vous sous 48 heures pour
                                    organiser un premier échange autour de votre projet.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setSubmitted(false)}
                                    data-testid="quote-new"
                                    className="mt-10 text-white/80 link-underline text-sm tracking-wide"
                                >
                                    Envoyer une nouvelle demande
                                </button>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                                <div>
                                    <label className="overline text-white/50">Nom *</label>
                                    <input
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        required
                                        data-testid="quote-name"
                                        className="field field--dark"
                                        placeholder="Votre nom"
                                    />
                                </div>
                                <div>
                                    <label className="overline text-white/50">Email *</label>
                                    <input
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        required
                                        data-testid="quote-email"
                                        className="field field--dark"
                                        placeholder="votre@email.com"
                                    />
                                </div>
                                <div>
                                    <label className="overline text-white/50">Téléphone</label>
                                    <input
                                        name="phone"
                                        value={form.phone}
                                        onChange={onChange}
                                        data-testid="quote-phone"
                                        className="field field--dark"
                                        placeholder="+32 ..."
                                    />
                                </div>
                                <div>
                                    <label className="overline text-white/50">Type de projet</label>
                                    <select
                                        name="project_type"
                                        value={form.project_type}
                                        onChange={onChange}
                                        data-testid="quote-project-type"
                                        className="field field--dark bg-transparent"
                                    >
                                        <option value="" className="bg-[#0d1b2e]">Sélectionner</option>
                                        {PROJECT_TYPES.map((p) => (
                                            <option key={p} value={p} className="bg-[#0d1b2e]">
                                                {p}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="md:col-span-2 mt-4">
                                    <label className="overline text-white/50">Votre projet *</label>
                                    <textarea
                                        name="message"
                                        value={form.message}
                                        onChange={onChange}
                                        required
                                        rows={4}
                                        data-testid="quote-message"
                                        className="field field--dark resize-none"
                                        placeholder="Décrivez-nous votre projet, vos envies, le contexte..."
                                    />
                                </div>
                                <div className="md:col-span-2 mt-8 flex items-center justify-between gap-6 flex-wrap">
                                    <p className="text-white/45 text-xs max-w-sm leading-relaxed">
                                        En envoyant ce formulaire, vous acceptez d'être recontacté
                                        par notre studio. Vos données restent confidentielles.
                                    </p>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="quote-submit"
                                        className="btn-accent group disabled:opacity-60"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" />
                                                Envoi…
                                            </>
                                        ) : (
                                            <>
                                                Obtenir un devis gratuit
                                                <ArrowRight size={18} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </motion.form>
                </div>
            </div>
        </section>
    );
}
